import React from 'react';

function AdminDonations({ supabaseClient }) {
    const [donations, setDonations] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [filter, setFilter] = React.useState('all'); // all, pending, approved, completed, rejected
    const [selectedDonation, setSelectedDonation] = React.useState(null);
    const [showDetailsModal, setShowDetailsModal] = React.useState(false);
    const [showUpdateModal, setShowUpdateModal] = React.useState(false);
    const [updateStatus, setUpdateStatus] = React.useState('');
    const [updateNotes, setUpdateNotes] = React.useState('');

    React.useEffect(() => {
        fetchDonations();
    }, [filter, supabaseClient]);

    const fetchDonations = async () => {
        if (!supabaseClient) return;
        
        setIsLoading(true);
        setError(null);

        try {
            let donationsData = [];
            let query = supabaseClient
                .from('donations')
                .select('*');
                
            if (filter !== 'all') {
                query = query.eq('status', filter);
            }
            
            const { data, error } = await query;
            
            if (error) throw error;
            
            donationsData = data || [];
            
            // Process the data to parse JSON hardware field
            const processedData = donationsData.map(donation => {
                let hardwareItems = [];
                try {
                    // Parse hardware JSON string if it exists
                    if (donation.hardware && typeof donation.hardware === 'string') {
                        hardwareItems = JSON.parse(donation.hardware);
                    } else if (Array.isArray(donation.hardware)) {
                        hardwareItems = donation.hardware;
                    }
                } catch (e) {
                    console.error('Error parsing hardware JSON:', e);
                }
                
                return {
                    ...donation,
                    parsedHardware: hardwareItems
                };
            });
            
            setDonations(processedData);
        } catch (err) {
            console.error('Error fetching donations:', err);
            setError('Failed to load donations. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleViewDetails = (donation) => {
        setSelectedDonation(donation);
        setShowDetailsModal(true);
    };

    const handleCloseDetailsModal = () => {
        setShowDetailsModal(false);
    };

    const handleOpenUpdateModal = (donation) => {
        setSelectedDonation(donation);
        setUpdateStatus(donation.status || 'pending');
        setUpdateNotes(donation.admin_notes || '');
        setShowUpdateModal(true);
    };

    const handleCloseUpdateModal = () => {
        setShowUpdateModal(false);
    };

    const handleUpdateDonation = async (e) => {
        e.preventDefault();
        
        if (!selectedDonation || !supabaseClient) return;
        const donationId = String(selectedDonation.id).replace(/:.*$/, '');
        try {
            const response = await supabaseClient
                .from('donations')
                .update({
                    status: updateStatus,
                    admin_notes: updateNotes,
                    updated_at: new Date().toISOString()
                })
                .eq('id', donationId);
            // Log the full response for debugging
            console.log('Supabase update response:', response);
            if (response.error) {
                console.error('Supabase update error:', response.error);
                alert('Failed to update donation: ' + (response.error.message || 'Unknown error'));
                return;
            }
            setDonations(donations.map(donation => 
                donation.id === selectedDonation.id ? 
                { 
                    ...donation, 
                    status: updateStatus, 
                    admin_notes: updateNotes 
                } : 
                donation
            ));
            handleCloseUpdateModal();
        } catch (err) {
            console.error('Error updating donation (exception):', err);
            alert('Failed to update donation. Please try again.');
        }
    };

    const handleDeleteDonation = async (donationId) => {
        if (!supabaseClient) return;
        
        if (!confirm('Are you sure you want to delete this donation? This action cannot be undone.')) {
            return;
        }
        
        try {
            const response = await supabaseClient
                .from('donations')
                .delete()
                .eq('id', donationId);
                
            const error = response.error;
            if (error) throw error;
            
            // Remove the donation from the array locally
            setDonations(donations.filter(donation => donation.id !== donationId));
            
            // Close the modal if the deleted donation is currently selected
            if (selectedDonation && selectedDonation.id === donationId) {
                handleCloseDetailsModal();
                handleCloseUpdateModal();
            }
        } catch (err) {
            console.error('Error deleting donation:', err);
            alert('Failed to delete donation. Please try again.');
        }
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        
        try {
            const options = { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            };
            return new Date(dateString).toLocaleDateString(undefined, options);
        } catch (e) {
            return 'Invalid date';
        }
    };

    // Get status badge color based on status
    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'pending':
                return 'bg-yellow-100 text-yellow-800';
            case 'approved':
                return 'bg-blue-100 text-blue-800';
            case 'completed':
                return 'bg-green-100 text-green-800';
            case 'rejected':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    // Calculate total quantity from hardware items
    const getTotalQuantity = (hardwareItems) => {
        if (!hardwareItems || !Array.isArray(hardwareItems)) return '1';
        return hardwareItems.reduce((total, item) => total + (parseInt(item.quantity) || 1), 0).toString();
    };

    // Format hardware items for display
    const formatHardwareDisplay = (hardwareItems) => {
        if (!hardwareItems || !Array.isArray(hardwareItems) || hardwareItems.length === 0) {
            return 'Hardware';
        }
        
        return hardwareItems.map(item => `${item.type || 'Item'} (${item.quantity || 1})`).join(', ');
    };

    if (isLoading && donations.length === 0) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 p-6 rounded-lg">
                <h2 className="text-xl font-bold text-red-700">Error</h2>
                <p className="text-red-600">{error}</p>
                <button 
                    onClick={fetchDonations}
                    className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Hardware Donations</h2>
                <div className="flex space-x-2">
                    <button 
                        onClick={() => setFilter('all')}
                        className={`px-3 py-1 rounded ${
                            filter === 'all' ? 
                            'bg-gray-800 text-white' : 
                            'bg-gray-200 text-gray-800 hover:bg-gray-300'
                        }`}
                    >
                        All
                    </button>
                    <button 
                        onClick={() => setFilter('pending')}
                        className={`px-3 py-1 rounded ${
                            filter === 'pending' ? 
                            'bg-yellow-500 text-white' : 
                            'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                        }`}
                    >
                        Pending
                    </button>
                    <button 
                        onClick={() => setFilter('approved')}
                        className={`px-3 py-1 rounded ${
                            filter === 'approved' ? 
                            'bg-blue-500 text-white' : 
                            'bg-blue-100 text-blue-800 hover:bg-blue-200'
                        }`}
                    >
                        Approved
                    </button>
                    <button 
                        onClick={() => setFilter('completed')}
                        className={`px-3 py-1 rounded ${
                            filter === 'completed' ? 
                            'bg-green-500 text-white' : 
                            'bg-green-100 text-green-800 hover:bg-green-200'
                        }`}
                    >
                        Completed
                    </button>
                    <button 
                        onClick={() => setFilter('rejected')}
                        className={`px-3 py-1 rounded ${
                            filter === 'rejected' ? 
                            'bg-red-500 text-white' : 
                            'bg-red-100 text-red-800 hover:bg-red-200'
                        }`}
                    >
                        Rejected
                    </button>
                </div>
            </div>

            {/* Donations Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Donor
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Hardware
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Submitted
                                </th>
                                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {donations.length > 0 ? (
                                donations.map(donation => (
                                    <tr key={donation.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {donation.name || donation.user?.name || 'Anonymous'}
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        {donation.email || donation.user?.email || 'No Email'}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">
                                                {formatHardwareDisplay(donation.parsedHardware)}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                Quantity: {getTotalQuantity(donation.parsedHardware)}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(donation.status)}`}>
                                                {donation.status || 'pending'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatDate(donation.created_at)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button 
                                                onClick={() => handleViewDetails(donation)}
                                                className="text-blue-600 hover:text-blue-900 mr-3"
                                                title="View Details"
                                            >
                                                <i className="fas fa-eye"></i>
                                            </button>
                                            <button 
                                                onClick={() => handleOpenUpdateModal(donation)}
                                                className="text-green-600 hover:text-green-900 mr-3"
                                                title="Update Status"
                                            >
                                                <i className="fas fa-edit"></i>
                                            </button>
                                            <button 
                                                onClick={() => handleDeleteDonation(donation.id)}
                                                className="text-red-600 hover:text-red-900"
                                                title="Delete"
                                            >
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                                        {filter === 'all' ? (
                                            'No donations found.'
                                        ) : (
                                            `No ${filter} donations found.`
                                        )}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* View Donation Details Modal */}
            {showDetailsModal && selectedDonation && (
                <div className="fixed inset-0 overflow-y-auto bg-gray-800 bg-opacity-75 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-xl w-full p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold">Donation Details</h3>
                            <button 
                                onClick={handleCloseDetailsModal}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        
                        <div className="mb-6">
                            <div className="bg-gray-100 p-4 rounded-lg mb-4">
                                <h4 className="text-lg font-semibold mb-2">Donor Information</h4>
                                <p><strong>Name:</strong> {selectedDonation.name || selectedDonation.user?.name || 'Anonymous'}</p>
                                <p><strong>Email:</strong> {selectedDonation.email || selectedDonation.user?.email || 'N/A'}</p>
                                <p><strong>Phone:</strong> {selectedDonation.phone || 'N/A'}</p>
                                <p><strong>Address:</strong> {selectedDonation.address || 'N/A'}</p>
                                {selectedDonation.city && <p><strong>City:</strong> {selectedDonation.city}</p>}
                                {selectedDonation.state && <p><strong>State:</strong> {selectedDonation.state}</p>}
                                {selectedDonation.zip && <p><strong>ZIP:</strong> {selectedDonation.zip}</p>}
                            </div>
                            
                            <div className="bg-gray-100 p-4 rounded-lg mb-4">
                                <h4 className="text-lg font-semibold mb-2">Hardware Details</h4>
                                {selectedDonation.parsedHardware && selectedDonation.parsedHardware.length > 0 ? (
                                    <div>
                                        <table className="min-w-full divide-y divide-gray-200 mb-2">
                                            <thead>
                                                <tr>
                                                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {selectedDonation.parsedHardware.map((item, index) => (
                                                    <tr key={index}>
                                                        <td className="py-2">{item.type || 'Item'}</td>
                                                        <td className="py-2">{item.quantity || 1}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        <p><strong>Total Items:</strong> {getTotalQuantity(selectedDonation.parsedHardware)}</p>
                                    </div>
                                ) : (
                                    <p>No hardware details available</p>
                                )}
                                <p><strong>Description:</strong> {selectedDonation.description || 'N/A'}</p>
                                {selectedDonation.pickup_date && (
                                    <p><strong>Pickup Date:</strong> {new Date(selectedDonation.pickup_date).toLocaleDateString()}</p>
                                )}
                                {selectedDonation.pickup_time && <p><strong>Pickup Time:</strong> {selectedDonation.pickup_time}</p>}
                            </div>
                            
                            <div className="bg-gray-100 p-4 rounded-lg">
                                <h4 className="text-lg font-semibold mb-2">Status Information</h4>
                                <p>
                                    <strong>Status:</strong> 
                                    <span className={`ml-2 px-2 py-1 text-xs rounded-full ${getStatusBadgeClass(selectedDonation.status)}`}>
                                        {selectedDonation.status || 'pending'}
                                    </span>
                                </p>
                                <p><strong>Submitted:</strong> {formatDate(selectedDonation.created_at)}</p>
                                <p><strong>Last Updated:</strong> {formatDate(selectedDonation.updated_at)}</p>
                                <p><strong>Admin Notes:</strong> {selectedDonation.admin_notes || 'No notes'}</p>
                                {selectedDonation.impact_score && (
                                    <p><strong>Impact Score:</strong> {selectedDonation.impact_score}</p>
                                )}
                            </div>
                        </div>
                        
                        <div className="flex justify-end">
                            <button 
                                onClick={handleCloseDetailsModal}
                                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 mr-2 hover:bg-gray-50"
                            >
                                Close
                            </button>
                            <button 
                                onClick={() => {
                                    handleCloseDetailsModal();
                                    handleOpenUpdateModal(selectedDonation);
                                }}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                                Update Status
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Update Status Modal */}
            {showUpdateModal && selectedDonation && (
                <div className="fixed inset-0 overflow-y-auto bg-gray-800 bg-opacity-75 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold">Update Donation Status</h3>
                            <button 
                                onClick={handleCloseUpdateModal}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        
                        <form onSubmit={handleUpdateDonation}>
                            <div className="mb-4">
                                <label className="block text-gray-700 text-sm font-bold mb-2">
                                    Status
                                </label>
                                <select 
                                    value={updateStatus}
                                    onChange={(e) => setUpdateStatus(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    required
                                >
                                    <option value="pending">Pending</option>
                                    <option value="approved">Approved</option>
                                    <option value="completed">Completed</option>
                                    <option value="rejected">Rejected</option>
                                </select>
                            </div>
                            
                            <div className="mb-6">
                                <label className="block text-gray-700 text-sm font-bold mb-2">
                                    Admin Notes
                                </label>
                                <textarea 
                                    value={updateNotes}
                                    onChange={(e) => setUpdateNotes(e.target.value)}
                                    rows="4"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Add notes about this donation (visible only to admins)"
                                ></textarea>
                            </div>
                            
                            <div className="flex justify-end">
                                <button 
                                    type="button"
                                    onClick={handleCloseUpdateModal}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 mr-2 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                >
                                    Save Changes
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

window.AdminDonations = AdminDonations;

export default AdminDonations;
